import Decimal from 'decimal.js';
import { isWeighedLine } from './delivery';

/**
 * P11 B5 PR-A – Dữ liệu nền cho điều phối ghép tải đa đơn (docs/phases/P11.md §10, DEC-260).
 * Hàm thuần: khối hữu dụng thùng xe, chiều dài hàng chở được, khối tích 1 ĐVT sản phẩm (nhập tay hoặc ước tính ống).
 * Thiếu số thì trả null ("chưa đủ dữ liệu"), không đoán; bộ ghép chuyến (PR sau) coi null là phải kiểm tay.
 */
const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });

export const GEO_SOURCES = ['manual', 'link', 'osm', 'google'] as const;
export type GeoSource = (typeof GEO_SOURCES)[number];
export const GEO_SOURCE_LABELS: Record<GeoSource, string> = {
  manual: 'Gõ tay',
  link: 'Link Google Maps',
  osm: 'Tìm theo địa chỉ (OpenStreetMap)',
  google: 'Tìm theo địa chỉ (Google)',
};

export interface CargoBox {
  cargoLengthM?: number | null;
  cargoWidthM?: number | null;
  cargoHeightM?: number | null;
  usableVolumeM3?: number | null;
  overhangMaxM?: number | null;
}

/** Khối xếp được của thùng (m³): khai báo riêng, không có thì dài × rộng × cao; thiếu số → null. */
export function vehicleUsableVolume(v: CargoBox): number | null {
  if (v.usableVolumeM3 && v.usableVolumeM3 > 0) return v.usableVolumeM3;
  const { cargoLengthM: l, cargoWidthM: w, cargoHeightM: h } = v;
  if (!l || !w || !h) return null;
  return new D(l).times(w).times(h).toDecimalPlaces(2).toNumber();
}

/** Hàng dài nhất xe chở được (m) = dài thùng + phần được thò ra sau; chưa khai dài thùng → null. */
export function vehicleMaxItemLength(v: CargoBox): number | null {
  if (!v.cargoLengthM) return null;
  return new D(v.cargoLengthM).plus(v.overhangMaxM ?? 0).toDecimalPlaces(2).toNumber();
}

export interface ProductVolumeInput {
  /** Khối tích 1 ĐVT chính nhập tay (m³) – ưu tiên */
  volumeM3?: number | null;
  odMm?: number | null;
  lengthM?: number | null;
  /** ĐVT chính (dòng ĐVT Kg thì không ước tính được theo cây) */
  unit?: string | null;
}

export type VolumeSource = 'product' | 'pipe_estimate';

/**
 * Khối tích 1 ĐVT (m³). Nhập tay trên sản phẩm thì dùng số đó. Ống (có OD + dài, ĐVT không phải Kg) ước tính
 * theo bó xếp vuông: OD² × dài (chiếm chỗ thực khi xếp lên thùng, lớn hơn thể tích ống). Còn lại → null.
 */
export function productUnitVolume(p: ProductVolumeInput): { volumeM3: number; source: VolumeSource } | null {
  if (p.volumeM3 && p.volumeM3 > 0) return { volumeM3: p.volumeM3, source: 'product' };
  if (p.odMm && p.odMm > 0 && p.lengthM && p.lengthM > 0 && !isWeighedLine(p.unit)) {
    const od = new D(p.odMm).div(1000);
    return { volumeM3: od.times(od).times(p.lengthM).toDecimalPlaces(6).toNumber(), source: 'pipe_estimate' };
  }
  return null;
}

/** Khối tích một dòng = số lượng (ĐVT chính) × khối 1 ĐVT; thiếu khối → null. */
export function lineVolume(baseQuantity: number, unitVolumeM3: number | null | undefined): number | null {
  if (unitVolumeM3 == null) return null;
  if (!(baseQuantity > 0)) return 0;
  return new D(baseQuantity).times(unitVolumeM3).toDecimalPlaces(3).toNumber();
}

/** Tọa độ chuẩn 6 số lẻ (≈ 0,1 m), kiểm phạm vi; sai → null. */
export function normalizeLatLng(lat: unknown, lng: unknown): { lat: number; lng: number } | null {
  const a = Number(lat);
  const b = Number(lng);
  if (lat === null || lng === null || lat === '' || lng === '' || !Number.isFinite(a) || !Number.isFinite(b)) return null;
  if (Math.abs(a) > 90 || Math.abs(b) > 180) return null;
  return { lat: new D(a).toDecimalPlaces(6).toNumber(), lng: new D(b).toDecimalPlaces(6).toNumber() };
}
